import { useState } from "react";
import { formatBirth, type ReadingResponse, type UserView } from "../api";
import { Logo } from "../icons";
import { DateField, isFullDate } from "../ui";
import { api } from "../api";
import type { Loader } from "./Result";

export interface CalcKind {
  id: string;
  moduleId: string | null;
  chip: string;
  head: string;
  /** own — по дате из профиля; other — нужна вторая дата (партнёр или ребёнок). */
  input: "own" | "pair" | "child" | "city";
  otherLabel?: string;
}

export const CALC_KINDS: CalcKind[] = [
  { id: "star", moduleId: "star", chip: "Звезда личности", head: "Открой звезду, чтобы узнать своё предназначение", input: "own" },
  { id: "compat", moduleId: "compatibility", chip: "Совместимость", head: "Узнай, что связывает вас с партнёром, другом или коллегой", input: "pair", otherLabel: "Дата рождения второго человека" },
  { id: "money", moduleId: "money_archetypes", chip: "Код денег", head: "Узнай, через какую деятельность к тебе приходят деньги", input: "own" },
  { id: "city", moduleId: null, chip: "Место жительства", head: "Проверь, насколько тебе подходит город", input: "city" },
  { id: "child", moduleId: "child_profile", chip: "Детская матрица", head: "Узнай сильные стороны ребёнка и как их поддержать", input: "child", otherLabel: "Дата рождения ребёнка" },
  { id: "success", moduleId: "success_code", chip: "Код успеха", head: "Три сквозных урока и совпадение с кодом дня", input: "own" },
  { id: "inner", moduleId: "inner_child", chip: "Внутренний ребёнок", head: "Узнай, какой детский сценарий влияет на тебя сегодня", input: "own" },
  { id: "type", moduleId: "consciousness_mission", chip: "Тип личности", head: "Узнай свой тип личности и миссию", input: "own" },
  { id: "code366", moduleId: "code366", chip: "Код дня рождения", head: "Узнай свой личный код на день и месяц рождения", input: "own" },
];

interface Props {
  user: UserView;
  initial: string;
  onResult: (title: string, moduleId: string, loader: Loader) => void;
}

/** Вкладка «Расчёты»: пилюли разборов, форма с датой, кнопка «Рассчитать». */
export function Calc({ user, initial, onResult }: Props) {
  const [kindId, setKindId] = useState(initial);
  const [other, setOther] = useState("");
  const [city, setCity] = useState("");
  const kind = CALC_KINDS.find((k) => k.id === kindId) ?? CALC_KINDS[0]!;
  const needsOther = kind.input === "pair" || kind.input === "child";
  const ready = kind.input === "city" ? false : needsOther ? isFullDate(other) : true;

  const submit = () => {
    if (!kind.moduleId) return;
    const moduleId = kind.moduleId;
    const loader: Loader = needsOther
      ? () => api.readingWith(moduleId, { otherBirthDate: other })
      : () => api.reading(moduleId);
    onResult(kind.chip, moduleId, loader as () => Promise<ReadingResponse>);
  };

  return (
    <div className="warm">
      <div className="pagehead">
        <h1>Расчёты</h1>
        <p>Открой тайны своей жизни и получи рекомендации</p>
      </div>
      <div className="chiprow">
        {CALC_KINDS.map((k) => (
          <button key={k.id} className={`pill${k.id === kindId ? " on" : ""}`} onClick={() => setKindId(k.id)}>
            {k.chip}
          </button>
        ))}
      </div>
      <div className="sheet" style={{ minHeight: "62vh" }}>
        <h2 className="formhead" style={{ color: "var(--accent-deep)" }}>
          {kind.head}
        </h2>
        {kind.input === "city" ? (
          <>
            <label className="field">
              <span>Название города</span>
              <input className="text" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Например, Минск" />
            </label>
            <div className="empty">
              <Logo />
              <p>Расчёт числа города готовится: нужна таблица соответствия букв числам. Скоро добавим.</p>
            </div>
          </>
        ) : (
          <>
            {needsOther && kind.input === "pair" && <DateField label="Твоя дата рождения" value={formatBirth(user.birthDate)} readOnly />}
            {needsOther ? (
              <DateField label={kind.otherLabel ?? "Дата рождения"} value={other} onChange={setOther} />
            ) : (
              <DateField label="Дата рождения" value={formatBirth(user.birthDate)} readOnly />
            )}
            {needsOther ? (
              <p className="hint">🔒 Вторая дата используется только для расчёта и не сохраняется.</p>
            ) : (
              <p className="hint">Расчёт идёт по дате рождения из вашего профиля.</p>
            )}
            <div className="formspace" />
            <button className="primary" disabled={!ready} onClick={submit}>
              Рассчитать
            </button>
          </>
        )}
      </div>
    </div>
  );
}
